import {Component, EventEmitter, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {User} from "../../../../../model/user";
import {AuthenticationService} from "../../../../../services/apiServices/security/authentication.service";
import {Lre} from "../../../../../model/lre";
import {CreateDepositService} from "../../../../../services/apiServices/business/create-deposit.service";
import {Unsubscribable} from "../../../../mixin/unsubscribable.component";
import {SelectItem} from "primeng/api";
import {DropdownService} from "../../../../../services/crossComponent/drop-down.service";
import {Observable} from "rxjs";
import {AddressLine} from "../../../../../model/address-line";
import {EmailValidator} from "../../../../../model/email-validator";
import {SignerQualityEnum, AuthTypeEnum} from '../../../../../enums/dropdown.enums';
import {ErlTypeEnum} from "../../../../../enums/deposit.enums";
import {LreSender} from "../../../../../model/lre-sender";
import {LreRecipient} from "../../../../../model/lre-recipient";

@Component({
  selector: 'app-deposit-erl-info',
  templateUrl: './deposit-erl-info.component.html',
  styleUrls: ['./deposit-erl-info.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DepositErlInfoComponent extends Unsubscribable(class {}) implements OnInit {

  public user: User;

  public lreObject: Lre;
  public erlType: ErlTypeEnum;
  public currentSenderAddress: any;
  public senderAddress: any;
  public openSenderAddress: boolean;

  public titleItems$: Observable<SelectItem[]> = this.dropdownService.getTitleItems().pipe(this.takeUntilDestroyed());
  public authTypeItems$: Observable<SelectItem[]> = this.dropdownService.getAuthTypeItems().pipe(this.takeUntilDestroyed());
  public signerQualityItems$: Observable<SelectItem[]> = this.dropdownService.getSignerQualityItems().pipe(this.takeUntilDestroyed());

  @Output() moveNext: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() infoERL: EventEmitter<Lre>= new EventEmitter<Lre>();
  @Output() typeErl: EventEmitter<ErlTypeEnum>= new EventEmitter<ErlTypeEnum>();
  @Output() addressSender: EventEmitter<any> = new EventEmitter<any>();

  constructor(private authenticationService: AuthenticationService,
              private authService: AuthenticationService,
              private createDepositService: CreateDepositService,
              private dropdownService: DropdownService) {
    super();
  }

  ngOnInit() {
    this.erlType = ErlTypeEnum.ERL_UNIT
    this.user = this.authenticationService.getCurrentUser();
    this.initLreObject();
  }

  private initLreObject(): void {
    this.lreObject = new Lre(this.authService.getCurrentUser().idCustomer);
    this.lreObject.recipient.signerQuality = SignerQualityEnum.PROFESSIONAL;
    this.lreObject.sender.signerQuality = SignerQualityEnum.PROFESSIONAL;
    this.createDepositService.getLREDefaultSender()
      .pipe(this.takeUntilDestroyed())
      .subscribe(defaultSender => {

      if (defaultSender) {
        const {
          title = '',
          firstName = '',
          lastName = '',
          company = '',
          mail: mailAddress = '',
          phone: phoneNumber = '',
          address = {}
        } = defaultSender;

        this.lreObject.sender.title = title;
        this.lreObject.sender.firstName = firstName;
        this.lreObject.sender.lastName = lastName;
        this.lreObject.sender.company = company;
        this.lreObject.sender.mailAddress = mailAddress;
        this.lreObject.sender.phoneNumber = phoneNumber;

        const { RECIPIENT, APARTMENT, BUILDING, STREET, SERVICE, POSTAL_CODE, CITY, COUNTRY } = address;

        const addressElements = [
          { key: 'addrElem1', value: RECIPIENT },
          { key: 'addrElem2', value: APARTMENT },
          { key: 'addrElem3', value: BUILDING },
          { key: 'addrElem4', value: STREET },
          { key: 'addrElem5', value: SERVICE },
          { key: 'zipcode', value: POSTAL_CODE },
          { key: 'city', value: CITY },
          { key: 'country', value: COUNTRY }
        ];

        const elementByName = {};
        const elements = addressElements.reduce((acc, { key, value }) => {
          if (value) {
            acc.push(value);
            elementByName[key] = value;
          }
          return acc;
        }, []);

        if (elements.length > 0) {
          this.lreObject.sender.address.push(elements.join('\n'));
          this.currentSenderAddress = {
            sElem: elements,
            country: COUNTRY || 'FRANCE',
            mElemByName: elementByName
          };
        }
      }
    });
  }

  public nextEnabled(): boolean {
    // Vérifie les conditions pour un recipient
    const isRecipientValid = (recipient: LreRecipient): boolean => {
      if (!recipient) return false;

      const isProfessional = recipient.signerQuality === SignerQualityEnum.PROFESSIONAL;
      const isIndividual = recipient.signerQuality === SignerQualityEnum.INDIVIDUAL;

      const hasValidMail = !!this.isValidMail(recipient.mailAddress);  // Conversion en boolean
      const hasValidAuthentication = (
        (recipient.authenticationType === AuthTypeEnum.SMS && !!recipient.phoneNumber) ||  // Conversion en boolean
        (recipient.authenticationType === AuthTypeEnum.EMAIL && !recipient.phoneNumber)
      );

      const isValidProfessional = isProfessional && !!recipient.company && !recipient.title && !recipient.firstName && !recipient.lastName;
      const isValidIndividual = isIndividual && !!recipient.title && !!recipient.firstName && !!recipient.lastName && !recipient.company;

      return (isValidProfessional || isValidIndividual) && hasValidMail && hasValidAuthentication;
    };

    // Vérifie les conditions pour un sender
    const isSenderValid = (sender: LreSender): boolean => {
      if (!sender) return false;

      const isProfessional = sender.signerQuality === SignerQualityEnum.PROFESSIONAL;
      const isIndividual = sender.signerQuality === SignerQualityEnum.INDIVIDUAL;

      const hasValidMail = this.isValidMail(sender.mailAddress);
      const hasValidAuthentication = (
        (sender.authenticationType === AuthTypeEnum.SMS && sender.phoneNumber) ||
        (sender.authenticationType === AuthTypeEnum.EMAIL && !sender.phoneNumber)
      );

      const isValidProfessional = isProfessional && sender.company && !sender.title && !sender.firstName && !sender.lastName;
      const isValidIndividual = isIndividual && sender.title && sender.firstName && sender.lastName && !sender.company;

      return (isValidProfessional || isValidIndividual) && hasValidMail && hasValidAuthentication && this.isValidAddress(sender.address);
    };

    // Vérification des conditions basées sur le type de ERL
    const isRecipientCondition = this.erlType !== ErlTypeEnum.ERL_UNIT || isRecipientValid(this.lreObject.recipient);
    const isSenderCondition = isSenderValid(this.lreObject.sender);

    // Vérification finale
    return !!this.lreObject.subject && isRecipientCondition && isSenderCondition;
  }

  public handleSenderAddress(address: any): void {
    this.currentSenderAddress = {
      sElem: address.fa,
      mElemByName: this.addressLinesToMap(address.lines)
    };

    this.senderAddress = {
      lines:address.fa.join('\n'),
      countryISOId: address.country === "FRANCE:NO-STATE" ? "FR" : null,
      elements: this.addressLinesToMap(address.lines)
    }

    this.lreObject.sender.address = [];
    this.lreObject.sender.address.push(address.fa.join('\n'));
  }

  public showSenderAddress() {
    this.openSenderAddress = true;
  }

  public handleSenderAddressClose(result: boolean): void {
    this.openSenderAddress = result;
  }

  public isValidMail(mail: string): boolean {
    return EmailValidator.emailIsValid(mail);
  }

  public isValidAddress(address: string[]): boolean {
    return address && address.length > 0 && address[0].split('\n').length >= 2;
  }

  private addressLinesToMap(lines: AddressLine[]): any {
    const map: any = {};
    for (const line of lines) {
      map[line.sDataName] = line.sDataValue;
    }
    return map;
  }

  public nextStep(): void {
    this.moveNext.emit(true);
    this.infoERL.emit(this.lreObject);
    this.typeErl.emit(this.erlType);
    this.addressSender.emit(this.senderAddress);
  }

  public onChangeErlType() {
    if(this.erlType === ErlTypeEnum.ERL_PUBLI) {
      this.lreObject.recipient.title = "";
      this.lreObject.recipient.firstName = "";
      this.lreObject.recipient.lastName = "";
      this.lreObject.recipient.company = "";
      this.lreObject.recipient.mailAddress = "";
      this.lreObject.recipient.authenticationType = "";
      this.lreObject.recipient.phoneNumber = "";
    }
  }

  public onTypeActorChange(type: 'sender' | 'recipient', newValue: string): void {
    const actor = this.lreObject[type];

    if (newValue === SignerQualityEnum.PROFESSIONAL) {
      actor.title = "";
      actor.firstName = "";
      actor.lastName = "";
    } else {
      actor.company = "";
    }
  }

  public onAuthenticationChange(type: 'sender' | 'recipient', newAuthentication: string): void {
    const actor = this.lreObject[type];

    if (newAuthentication === AuthTypeEnum.EMAIL) {
      actor.phoneNumber = "";
    }
  }

  protected readonly SignerQualityEnum = SignerQualityEnum;
  protected readonly AuthTypeEnum = AuthTypeEnum;
  protected readonly ErlTypeEnum = ErlTypeEnum;
}
